import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { buildNamePreview, NameParts } from "../utils/names";
import { Switch } from "./ui-kit/fields/switch";
import { TextInput } from "./ui-kit/fields/text-input";
import { FormField } from "./ui-kit/form/form-field";

export type NameFieldsModel = NameParts;

/**
 * Self-contained name fields section.
 * By default shows given_name and family_name.
 * A "Full name details" toggle expands into a 3×3 grid
 * following the natural reading order of a formal name.
 */
export const NameFields = observer(function NameFields({
  model,
}: {
  model: NameFieldsModel;
}) {
  const hasOptionalValues = Boolean(
    model.title_before ||
    model.title_after ||
    model.middle_names ||
    model.name_particle ||
    model.additional_family_names ||
    model.name_suffix ||
    model.name_order_given_first === false,
  );

  const [expanded, setExpanded] = useState(hasOptionalValues);
  const { t } = useLingui();

  const namePreview = buildNamePreview(model);

  return (
    <div className="flex flex-col gap-4">
      {expanded || hasOptionalValues ? (
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <FormField
              label={t`Title`}
              tooltip={t`e.g. Prof., Dr., Mr.`}
              className="w-24"
            >
              <TextInput attr="title_before" model={model} />
            </FormField>
            <FormField label={t`Given name`} mandatory className="flex-1">
              <TextInput attr="given_name" model={model} required />
            </FormField>
            <FormField label={t`Middle names`} className="flex-1">
              <TextInput attr="middle_names" model={model} />
            </FormField>
          </div>

          <div className="flex gap-3">
            <FormField
              label={t`Particle`}
              tooltip={t`e.g. van, von, de, al-`}
              className="w-24"
            >
              <TextInput attr="name_particle" model={model} />
            </FormField>
            <FormField label={t`Family name`} mandatory className="flex-1">
              <TextInput attr="family_name" model={model} required />
            </FormField>
            <FormField
              label={t`Add. family names`}
              tooltip={t`e.g. birth name, née`}
              className="flex-1"
            >
              <TextInput attr="additional_family_names" model={model} />
            </FormField>
          </div>

          {/* Row 3: what trails after */}
          <div className="flex gap-3">
            <FormField
              label={t`Suffix`}
              tooltip={t`e.g. Jr., Sr., III`}
              className="w-24"
            >
              <TextInput attr="name_suffix" model={model} />
            </FormField>
            <FormField
              label={t`Title after`}
              tooltip={t`e.g. PhD, CSc., MBA`}
              className="w-32"
            >
              <TextInput attr="title_after" model={model} />
            </FormField>
            <FormField
              label={t`Given name first`}
              tooltip={t`Controls the display order of the name`}
            >
              <Switch attr="name_order_given_first" model={model} />
            </FormField>
          </div>

          {namePreview && (
            <p className="text-muted-foreground text-sm italic">
              <Trans>Name preview: {namePreview}</Trans>
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <FormField label={t`Given name`} mandatory>
            <TextInput attr="given_name" model={model} required />
          </FormField>
          <FormField label={t`Family name`} mandatory>
            <TextInput attr="family_name" model={model} required />
          </FormField>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm">
        <Switch
          value={expanded || hasOptionalValues}
          onChange={setExpanded}
          disabled={hasOptionalValues}
        />
        <Trans>Full name details</Trans>
      </label>
    </div>
  );
});
